<template>
  <div class="icon-grid" role="radiogroup" :aria-label="ariaLabel">
    <button
      v-for="(icon, index) in icons"
      :key="icon.url"
      type="button"
      class="icon-btn"
      :class="{ selected: selectedIndex === index }"
      role="radio"
      :title="icon.label"
      :aria-label="icon.label"
      :aria-checked="String(selectedIndex === index)"
      :tabindex="selectedIndex === index ? 0 : -1"
      @click="selectIcon(icon.url)"
      @keydown="handleKeydown($event, index)"
    >
      <img :src="icon.url" :alt="icon.label" loading="lazy" />
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue"

const props = defineProps({
  modelValue: { type: String, default: "" },
  icons: { type: Array, required: true },
  ariaLabel: { type: String, default: "Icon picker" },
})

const emit = defineEmits(["update:modelValue"])
const selectedIndex = computed(() => {
  const index = props.icons.findIndex((icon) => icon.url === props.modelValue)
  return index >= 0 ? index : 0
})

function selectIcon(url) {
  emit("update:modelValue", url)
}

function handleKeydown(event, currentIndex) {
  if (!props.icons.length) return

  let nextIndex
  if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (currentIndex + 1) % props.icons.length
  else if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (currentIndex - 1 + props.icons.length) % props.icons.length
  else if (event.key === "Home") nextIndex = 0
  else if (event.key === "End") nextIndex = props.icons.length - 1
  else return

  event.preventDefault()
  selectIcon(props.icons[nextIndex].url)
  event.currentTarget.closest('[role="radiogroup"]')?.querySelectorAll('[role="radio"]')[nextIndex]?.focus()
}
</script>

<style scoped>
.icon-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
  max-height: 220px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 6px;
  background: #fff;
}
.icon-btn {
  display: grid;
  place-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  height: 34px;
  cursor: pointer;
  padding: 0;
}
.icon-btn img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.icon-btn:hover { background: #f7f7f7; }
.icon-btn.selected {
  border-color: #6b9cff;
  background: #eef4ff;
}
</style>
